import { BlurhashCanvas } from 'react-blurhash'
import { forwardRef, type ReactElement } from 'react'
import {
  Avatar,
  Box,
  createPolymorphicComponent,
  rem,
  Skeleton,
  type AvatarProps as MantineAvatarProps,
} from '@mantine/core'
import { Size } from '@front/model'

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | number | `${number}px`
export type WithAvatarSize = { size?: AvatarSize }

export type AvatarProps = {
  avatarBlurhash?: string
  avatarUrl?: string
  initials?: string
  size?: AvatarSize
  noBlurhash?: boolean
  color?: string
}

type Props = Omit<MantineAvatarProps, 'src' | 'size'> & AvatarProps

const sizes: Record<AvatarSize, string> = {
  xs: rem('24px'),
  sm: rem('32px'),
  md: rem('44px'),
  lg: rem('96px'),
}

const _AvatarBlurhash = forwardRef<HTMLDivElement, Props>(
  ({ avatarBlurhash, avatarUrl, initials, noBlurhash = false, color, ...rest }, ref) => {
    const { size } = rest
    const shouldShowInitials = !avatarBlurhash || noBlurhash

    return (
      <Box ref={ref} className='relative'>
        {avatarBlurhash && !noBlurhash && (
          <div className='absolute'>
            <BlurhashCanvas
              hash={avatarBlurhash}
              style={{
                borderRadius: Size.radius.full,
                width: getSize(size),
                height: getSize(size),
              }}
            />
          </div>
        )}
        <Avatar
          src={avatarBlurhash ? `${avatarUrl}?bh=${encodeURIComponent(avatarBlurhash)}` : undefined}
          variant='filled'
          color={color}
          {...rest}
          size={getSize(size)}
        >
          {shouldShowInitials && <span>{initials}</span>}
        </Avatar>
      </Box>
    )
  }
)

_AvatarBlurhash.displayName = 'AvatarBlurhash'

export const AvatarBlurhash = createPolymorphicComponent<'div', Props>(_AvatarBlurhash)

export const getSize = (size: AvatarSize = 'sm'): string | number => {
  if (typeof size === 'number') return rem(`${size}px`)
  if (size in sizes) return sizes[size]

  return size
}

export const AvatarSkeleton = ({ size }: WithAvatarSize): ReactElement => {
  return <Skeleton circle h={getSize(size)} w={getSize(size)} />
}
